import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  CleverPushInfrastructure,
  CleverPushNotification,
  CleverPushService,
  CleverPushTopic,
  CleverPushTopicType,
} from '@zero/feature-clever-push/base';
import { PushQuoteService } from '@zero/shared/ui-push';
import { AbstractAlertService, StoreErrorHandler } from '@zero/shared/util-global-state';
import { BackendResponseStatus, IBackendResponse } from '@zero/shared/util-models';
import { catchError, filter, from, map, of, switchMap, tap } from 'rxjs';
import { AlertingInfrastructure } from '../infrastructure/alerting.infrastructure';
import { alertingActions, alertingApiActions } from './alerting.actions';
import { alertingFeature } from './alerting.reducer';

@Injectable({ providedIn: 'root' })
export class AlertingEffects {
  readonly #store = inject(Store);
  readonly #actions$ = inject(Actions);
  readonly #cleverPushInfrastructure = inject(CleverPushInfrastructure);
  readonly #alertingInfrastructure = inject(AlertingInfrastructure);
  readonly #cleverPushService = inject(CleverPushService);
  readonly #pushQuoteService = inject(PushQuoteService);
  readonly #storeErrorHandler = inject(StoreErrorHandler);
  readonly #alertService = inject(AbstractAlertService);

  public loadTopics$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(alertingActions.loadTopics),
      switchMap(() =>
        this.#cleverPushInfrastructure.getTopics().pipe(
          map((response) => {
            const topics = response.reduce(
              (acc, topic) => {
                acc[topic.topicName] = topic;
                return acc;
              },
              {} as Record<string, CleverPushTopic>,
            );
            return alertingApiActions.loadTopicsSuccess({ topics });
          }),
          catchError((error: HttpErrorResponse) => of(alertingApiActions.loadTopicsFailure({ error }))),
        ),
      ),
    );
  });

  public setTopicSubscription$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(alertingActions.setTopicSubscription),
      switchMap((action) =>
        this.#cleverPushInfrastructure
          .putTopic({
            topicName: action.topicName,
            value: action.status,
          })
          .pipe(
            map(() => alertingApiActions.setTopicSubscriptionSuccess({ topicName: action.topicName, status: action.status })),
            catchError((error: HttpErrorResponse) =>
              of(alertingApiActions.setTopicSubscriptionFailure({ topicName: action.topicName, error })),
            ),
          ),
      ),
    );
  });

  public loadNotifications$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(alertingActions.loadNotifications),
      switchMap(() =>
        from(this.#cleverPushService.getNotifications()).pipe(
          map((notifications) =>
            notifications.reduce(
              (acc, notification) => ({ ...acc, [notification.id]: notification }),
              {} as Record<string, CleverPushNotification<CleverPushTopicType | undefined>>,
            ),
          ),
          map((notifications) => alertingActions.setNotifications({ notifications })),
          catchError(() => of(alertingActions.loadNotificationsFailure())),
        ),
      ),
    );
  });

  public loadPriceAlertOverview$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(
        alertingActions.loadPriceAlertOverview,
        alertingApiActions.updatePriceAlertDetailsSuccess,
        alertingApiActions.createPriceAlertSuccess,
      ),
      switchMap(() =>
        this.#alertingInfrastructure.getPriceAlertOverview().pipe(
          map((response) => alertingApiActions.loadPriceAlertOverviewSuccess({ priceAlertOverview: response })),
          catchError((error: HttpErrorResponse) => of(alertingApiActions.loadPriceAlertOverviewFailure({ error }))),
        ),
      ),
    );
  });

  public loadPriceAlertDetails$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(alertingActions.loadPriceAlertDetails),
      switchMap((action) =>
        this.#alertingInfrastructure.getPriceAlertDetails(action.isin).pipe(
          map((response) => alertingApiActions.loadPriceAlertDetailsSuccess({ priceAlert: response })),
          catchError((error: HttpErrorResponse) => {
            if ((error?.error as IBackendResponse<unknown>)?.status === BackendResponseStatus.ERROR) {
              return of(alertingApiActions.loadPriceAlertDetailsFailure({ error }));
            }

            return of(alertingApiActions.loadPriceAlertDetailsSuccess({ priceAlert: null }));
          }),
        ),
      ),
    );
  });

  public updatePriceAlertDetails$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(alertingActions.updatePriceAlertDetails),
      switchMap((action) =>
        this.#alertingInfrastructure.postPriceAlertDetails(action.priceAlert).pipe(
          map((response) => alertingApiActions.updatePriceAlertDetailsSuccess({ priceAlert: response })),
          catchError((error: HttpErrorResponse) => of(alertingApiActions.updatePriceAlertDetailsFailure({ error }))),
        ),
      ),
    );
  });

  public setVolaAlertActive$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(alertingActions.setVolaAlertActive),
      concatLatestFrom(() => this.#store.select(alertingFeature.selectSelectedPriceAlert)),
      map(([{ active }, priceAlert]) => {
        const volaAlert = priceAlert.volaAlert;
        return alertingActions.updatePriceAlertDetails({
          priceAlert: {
            ...priceAlert,
            volaAlert: {
              ...volaAlert,
              value: volaAlert.value ?? 2,
              active,
            },
          },
        });
      }),
    );
  });

  public setQuoteAlertActive = createEffect(() => {
    return this.#actions$.pipe(
      ofType(alertingActions.setQuoteAlertActive),
      concatLatestFrom(() => this.#store.select(alertingFeature.selectSelectedPriceAlert)),
      map(([{ active }, priceAlert]) => {
        const quoteAlerts = priceAlert.quoteAlerts;
        return alertingActions.updatePriceAlertDetails({
          priceAlert: {
            ...priceAlert,
            quoteAlerts: {
              ...quoteAlerts,
              active,
            },
          },
        });
      }),
    );
  });

  public createPriceAlert$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(alertingActions.createPriceAlert),
      switchMap(({ priceAlert, skipError }) =>
        this.#alertingInfrastructure.postPriceAlertDetails(priceAlert).pipe(
          map((response) => alertingApiActions.createPriceAlertSuccess({ priceAlert: response, withoutToast: true })),
          catchError((error: HttpErrorResponse) => of(alertingApiActions.createPriceAlertFailure({ error, skipError }))),
        ),
      ),
    );
  });

  public deletePriceAlert$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(alertingActions.deletePriceAlert),
      switchMap((action) =>
        this.#alertingInfrastructure.deletePriceAlert(action.isin).pipe(
          map(() => alertingApiActions.deletePriceAlertSuccess({ isin: action.isin })),
          catchError((error: HttpErrorResponse) => of(alertingApiActions.deletePriceAlertFailure({ error }))),
        ),
      ),
    );
  });

  public subscribeToPriceAlertDetailsQuote$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(alertingActions.subscribeToPriceAlertDetailsQuote),
      switchMap(({ isin }) =>
        this.#pushQuoteService.subscribeMultiBuffered([isin]).pipe(
          map((quoteRecord) => quoteRecord[isin]),
          filter((quote) => !!quote),
          map(({ ask, bid }) => alertingActions.updatePriceAlertQuote({ quote: (ask + bid) / 2 })),
        ),
      ),
    );
  });

  public priceAlertDeleteSuccess$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(alertingApiActions.deletePriceAlertSuccess),
        tap(() => this.#alertService.showSuccessAlert('Kurs-Alert gelöscht')),
      );
    },
    { dispatch: false },
  );

  public priceAlertCreatedSuccess$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(alertingApiActions.createPriceAlertSuccess),
        filter(({ withoutToast }) => !withoutToast),
        tap(() => this.#alertService.showSuccessAlert('Kurs-Alert angelegt')),
      );
    },
    { dispatch: false },
  );

  public priceAlertUpdateSuccess$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(alertingApiActions.updatePriceAlertDetailsSuccess),
        tap(() => this.#alertService.showSuccessAlert('Kurs-Alert gespeichert')),
      );
    },
    { dispatch: false },
  );

  public unsubscribeFromPriceAlertDetailsQuote$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(alertingActions.unsubscribeFromPriceAlertDetailsQuote),
        tap((action) => this.#pushQuoteService.unsubscribe(action.isin)),
      );
    },
    { dispatch: false },
  );

  public handleBackendError$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(
          alertingApiActions.loadTopicsFailure,
          alertingApiActions.setTopicSubscriptionFailure,
          alertingApiActions.loadPriceAlertOverviewFailure,
          alertingApiActions.loadPriceAlertDetailsFailure,
          alertingApiActions.updatePriceAlertDetailsFailure,
          alertingApiActions.deletePriceAlertFailure,
        ),
        tap(({ error }) => {
          this.#storeErrorHandler.handleBackendError(error);
        }),
      );
    },
    { dispatch: false },
  );

  public handleCreateBackendError$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(alertingApiActions.createPriceAlertFailure),
        tap(({ error, skipError }) => {
          if (skipError) {
            return;
          }
          this.#storeErrorHandler.handleBackendError(error);
        }),
      );
    },
    { dispatch: false },
  );
}
